import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import dayjs from 'dayjs';
import API from '@sesame/web-api';

import { ILoadState, IWarranty } from 'src/types';
import Alert, { AlertTypes } from 'src/components/AlertDisplay';
import ModalDialog from 'src/components/ModalDialog';

import { FormInput, FormSelect } from 'src/components/Form';
import { Spinner } from 'src/components/Spinner';
import { access } from 'fs';


// Make a string based object to ease form manipulatin
type IFormData = { [Property in keyof IWarranty]: string; } & { [key: string]: string }

type fieldNames = keyof IFormData;

interface IEditWarrantyDetailsProps {
  warrantyId?: number;
  onSave?: () => void;
}

export const EditWarrantyDetails = ({ warrantyId, onSave }: IEditWarrantyDetailsProps) => {
  const modalId = "warranty-modal-" + warrantyId || "0";

  return <ModalDialog
    id={modalId}
    buttonText={warrantyId ? '' : 'New'}
    title={warrantyId ? 'Edit Warranty' : 'Add Warranty'}
    icon={warrantyId ? 'pencil' : undefined}
  >
    <EditWarrantyDetailForm
      warrantyId={warrantyId}
      onSave={onSave}
      />
  </ModalDialog>

}
export const EditWarrantyDetailForm = ({ warrantyId, onSave }: IEditWarrantyDetailsProps) => {
  const  {isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [warranties, setWarranties] = useState<IWarranty[]>([]);
  const [displayState, setDisplayState] = useState<ILoadState>(ILoadState.NEW);
  const [formData, setFormData] = useState<IFormData>({
    warrantyId: '0',
    firstAvailableDate: '',
    lastAvailableDate: '',
    name: '',
    description: '',
    durationYears: '',
    serviceHours: '',
    serviceDays: '',
    supportResponseTime: '',
    onsiteResponseTime: '',
  });
  const [validation, setValidation] = useState<fieldNames[]>([]);

  const validate = () => {
    const valid: fieldNames[] = [];
    Object.keys(formData)
      .forEach(d => { if (formData[d].trim() == '') { valid.push(d) } });

    setValidation(valid);
    return valid.length;
  }
  const save = (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();

    if (validate() > 0) {
      return;
    }

    const newWarranty: IWarranty = {
      warrantyId: warrantyId || 0,
      firstAvailableDate: new Date(formData.firstAvailableDate),
      lastAvailableDate: new Date(formData.lastAvailableDate),
      name: formData.name,
      description: formData.description,
      durationYears: parseInt(formData.durationYears),
      serviceHours: formData.serviceHours,
      serviceDays: formData.serviceDays,
      supportResponseTime: formData.supportResponseTime,
      onsiteResponseTime: formData.onsiteResponseTime,
    };

    getAccessTokenSilently().then(accessToken => {
    if (warrantyId) {
      API.patch(accessToken, 'api/v1/warranty', newWarranty)
        .then((resp) => {
          Alert(AlertTypes.SUCCESS, 'Warranty Updated');
          if (onSave) {
            onSave();
          }
          const el = document.getElementById('modal-close');
          if (el) {
            el.click()
          }
        }).catch((err) => {
          Alert(AlertTypes.ERROR, 'Error updating warranty: ' + err.message);
        });
    } else {
      API.post(accessToken, 'api/v1/warranty', newWarranty)
        .then((resp) => {
          Alert(AlertTypes.SUCCESS, 'Warranty Saved');
          if (onSave) {
            onSave();
          }
          /*
          const el = document.getElementById(modalId);
          if (el) {
            el.click()
          }
            */
        }).catch((err) => {
          Alert(AlertTypes.ERROR, 'Error saving warranty: ' + err.message);

        });
    }
    })
  }

  const formChange = (item: fieldNames, event: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => {
    const newFormData = { ...formData };
    newFormData[item] = event.currentTarget.value;

    setFormData(newFormData);
  }

  const getWarranty = async (warratyId: number): Promise<IWarranty | void> => {
    const accessToken = await getAccessTokenSilently();
    const response = await API.get(accessToken, `api/v1/warranty/${warrantyId}`)
      .catch((err) => {
        if (err.status === 404) {
          Alert(AlertTypes.ERROR, 'Warranty Not Found');
          return;
        }
        console.log('Warrnanty err: ' + JSON.stringify(err));
        Alert(AlertTypes.ERROR, 'Warranty: ' + err.message);
      });
    const w = response.data as IWarranty
    return w;
  }

  useEffect(() => {
    // get other data
  }, [])

  useEffect(() => {
    if (warrantyId) {
      getWarranty(warrantyId)
        .then(w => {
          if (w) {
            const newFormData: IFormData = {
              warrantyId: `${warrantyId}`,
              firstAvailableDate: dayjs(w.firstAvailableDate).format('MM/DD/YYYY'),
              lastAvailableDate: dayjs(w.lastAvailableDate).format('MM/DD/YYYY'),
              name: w.name,
              description: w.description,
              durationYears: `${w.durationYears}`,
              serviceHours: w.serviceHours,
              serviceDays: w.serviceDays,
              supportResponseTime: w.supportResponseTime,
              onsiteResponseTime: w.onsiteResponseTime,
            };
            setFormData(newFormData);
            setDisplayState(ILoadState.READY);
          }
        });
      setDisplayState(ILoadState.LOADING);
    } else {
      /*
      const newFormData: IFormData = {
        warrantyId: '0',
        firstAvailableDate: '',
        lastAvailableDate: '',
        name: '',
        description: '',
        durationYears: '',
        serviceHours: '',
        serviceDays: '',
        supportResponseTime: '',
        onsiteResponseTime: '',
      }
      */
      
      setDisplayState(ILoadState.READY);
    }
  }, [warrantyId, getAccessTokenSilently]);


  if (isLoading || displayState == ILoadState.NEW || displayState == ILoadState.LOADING) {
    return <Spinner/>
  }

  return <form>
      <FormInput
        label="Name"
        name="name"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <FormInput
        label="Description"
        name="description"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <FormInput
        label="First Available Date"
        name="firstAvailableDate"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <FormInput
        label="Last Available Date"
        name="lastAvailableDate"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <FormInput
        label="Duration (Years)"
        name="durationYears"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <FormInput
        label="Service Hours e.g. 8-6 local time"
        name="serviceHours"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <FormInput
        label="Service Day e.g. M-F"
        name="serviceDays"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <FormInput
        label="Support Response Time e.g. 8 hours"
        name="supportResponseTime"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <FormInput
        label="Onsite Response Time e.g. Next Business Day"
        name="onsiteResponseTime"
        data={formData}
        changeFn={formChange}
        validation={validation}
      />

      <p className="uk-text-right">
        <button
          className="uk-button uk-button-primary uk-button-small"
          data-uk-toggle={`target: #warranty-modal-${warrantyId}`}
          onClick={(e) => save(e)}>
          Save
          </button>
      </p>
    </form>

}
